<template>
  <DpplNestedDropdown
    class="ml-2"
    :label="dropdownLabel"
    :children="filteredDropdownItems"
    is-top
    :disabled="!isDataLoaded"
    @top-mouse-enter="$emit('tracking-event', {
      action: 'editorDropdownHover',
      args: { label: dropdownLabel },
    })"
  >
    <template #topLabel>
      <v-btn
        variant="outlined"
        min-width="48"
        width="48"
        class="dropdown-button"
      >
        ⋮
      </v-btn>
    </template>
  </DpplNestedDropdown>
  <ApplyTemplateModal
    :job-id="jobId"
    :revision-id="revisionId"
    @tracking-event="$emit('tracking-event', $event)"
  />
  <DeleteModal
    :show="deleteModalVisible"
    :document-type="deleteModalText"
    :detailed-text="deleteModalText"
    :name="title"
    @close="deleteModalVisible = false"
    @delete="deleteJob"
  />
</template>

<script setup>
import DpplNestedDropdown from '@/components/datapeople/DpplNestedDropdown/DpplNestedDropdown.vue';
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { TYPE_TAPWAGE } from '@/helpers/constants/integrationTypes';
import { useEditor } from '@/stores/editor/editor';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useApplyTemplate } from '@/stores/document/applyTemplate';
import { recruitJsonDelete, recruitJsonAjaxAnalyze, recruitJsonDeleteTemplate } from '@/api/recruitJson';
import { camelCase } from 'lodash-es';
import { joinFooterAndHeaderFields } from '@/helpers/headerFooterFields';
import { shouldHideIfEmpty } from '@/helpers/constants/hideEmptyFields';
import ApplyTemplateModal from '@/components/shared/ApplyTemplateModal.vue';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import { writeToClipboardApi } from '@/helpers/copyToClipboard';
import { useAppAuthData } from '@/stores/app/authData';

// Parent/Child Interactions
const props = defineProps([
  'jobId',
  'title',
  'tapwageId',
  'superuser',
  'projectIntegrationType',
  'projectIntegrationTypeKey',
  'atsEditUrl',
  'hasIntegration',
  'isSharePage',
  'isTemplate',
  'isLinked',
  'isOpen',
  'isPublished',
  'isPrivate',
  'isDataLoaded',
  'isSyncable',
]);

const emits = defineEmits(['duplicate', 'tracking-event']);

// Router
const router = useRouter();
const route = useRoute();

// Stores
const editorStore = useEditor();
const {
  fields,
  documentType,
  isEditable,
  revisionId,
  showAtsMenu,
  showTapwageMenu,
  isFlagship,
} = storeToRefs(editorStore);
const { isAdmin } = storeToRefs(useAppAuthData());
const { checkRbacPermissions } = useAppAuthData();
const { copyFieldToClipboard, copyAllFieldsToClipboard, copyToClipboard } = editorStore;
const { setLoading } = useAppLoading();
const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();
const applyTemplateStore = useApplyTemplate();

// State
const deleteModalVisible = ref(false);

const shareKeyParam = computed(() => {
  const { shareKey } = window.sessionStorage;

  return shareKey ? `&key=${shareKey}` : '';
});

const dropdownLabel = computed(() => {
  if (props.isTemplate) {
    return 'Template';
  }
  return 'Job';
});

const showSyncOrExport = computed(() => !props.isTemplate && !props.isSharePage);

const fieldsWithFooterAndHeaderJoined = computed(() => joinFooterAndHeaderFields(fields.value)
  .filter((field) => !shouldHideIfEmpty({
    type: field.type, sections: field.sections, isTemplate: false,
  })));

const deleteModalText = computed(() => {
  if (documentType.value === 'job_template') return 'template';
  if (documentType.value === 'job' && isFlagship.value) return 'flagship job';
  return 'job post';
});
// Methods
const analyzeJob = async () => {
  emits('tracking-event', { action: 'editorLinkClick', args: { label: 'Analyze' } });
  setLoading({ pageLoading: true });

  try {
    const { jobId } = await recruitJsonAjaxAnalyze({
      jobId: props.jobId,
      revisionId: revisionId.value,
    });

    await router.replace({
      name: 'editor-view-reload',
      params: { id: jobId },
      query: { ...route.query },
    });
    addSuccessSnackbar({ text: `${dropdownLabel.value} successfully analyzed`, timeout: 3500 });
  } catch (e) {
    addErrorSnackbar({ text: `Analyze ${dropdownLabel.value} failed - ${e.message}` });
    throw e;
  } finally {
    setLoading({ pageLoading: false });
  }
};

const onCopyTitleClick = () => {
  copyToClipboard({ content: props.title });
  emits('tracking-event', { action: 'copyTitle', args: { job: props } });
};

const onCopyFieldClick = (id, field) => {
  copyFieldToClipboard(id);
  emits('tracking-event', {
    action: 'copyContent',
    args: { job: props, props: { field } },
  });
};

const onCopyAllFieldsClick = () => {
  copyAllFieldsToClipboard(props.title);
  emits('tracking-event', { action: 'copyAllFields', args: { job: props } });
};

const deleteJob = async () => {
  try {
    if (['job_template'].includes(documentType.value)) {
      const { success } = await recruitJsonDeleteTemplate({
        jobId: props.jobId,
      });

      if (success) {
        addSuccessSnackbar({
          text: `${props.title} has been deleted`,
        });
      }
    } else {
      await recruitJsonDelete({
        jobId: props.jobId,
      });
    }

    const documentTypeNames = {
      job_template: 'templates',
    };
    let name = 'document-library';

    if (documentTypeNames[documentType.value]) {
      name = documentTypeNames[documentType.value];
    }

    router.replace({ name });
  } catch (e) {
    addErrorSnackbar({ text: `Delete failed - ${e.message}` });
    throw e;
  } finally {
    deleteModalVisible.value = false;
  }
};

/**
 * The list represents all possible dropdown items, section (block) and
 * a condition for when an item should be displayed.
 * The last item in each section (except the very last item) should have
 * a divider rendered after it.
 * @returns {Array.<{name: string, available: boolean, section: number}>}
 * Array of dropdown items
 */
const dropdownItems = computed(() => [
  {
    name: 'template',
    label: 'Apply Template',
    section: 0,
    available: isEditable.value && documentType.value === 'job',
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: 'Apply Template' },
      });

      applyTemplateStore.setModalVisible(true);
    },
  },
  {
    name: 'newTemplate',
    label: 'New Template',
    section: 0,
    available: props.isTemplate && !props.isSharePage,
    onClick: () => router.push({
      name: 'templates',
      params: { from: String(props.jobId) },
    }),
  },
  {
    name: 'duplicate',
    label: 'Duplicate',
    section: 0,
    available: !props.isSharePage,
    onClick() {
      emits('duplicate');
    },
  },
  {
    name: 'analyze',
    label: 'Analyze',
    section: 0,
    available: checkRbacPermissions(['ajaxAnalyzeJob']) && !props.isSharePage,
    onClick: analyzeJob,
  },
  {
    name: 'delete',
    label: 'Delete',
    section: 0,
    available: (!props.isLinked || !props.hasIntegration)
      && !props.isSharePage
      && !(!props.isPrivate && !isAdmin.value) // only admin can delete public document
      && !(isFlagship.value && !isAdmin.value),
    onClick() {
      deleteModalVisible.value = true;
    },
  },
  {
    name: 'download',
    label: 'Download',
    section: 1,
    available: !showSyncOrExport.value || props.isSyncable,
    children: [
      {
        name: 'wordDocument',
        label: 'Word Document',
        onClick() {
          emits('tracking-event', {
            action: 'editorLinkClick',
            args: { label: 'Export: Word' },
          });

          window.open(`/recruit/download?id=${props.jobId}&format=word${shareKeyParam.value}`);
        },
      },
      {
        name: 'html',
        label: 'HTML',
        onClick() {
          emits('tracking-event', {
            action: 'editorLinkClick',
            args: { label: 'Export: HTML' },
          });

          window.open(`/recruit/download?id=${props.jobId}&format=html${shareKeyParam.value}`);
        },
      },
      {
        name: 'markdown',
        label: 'Markdown',
        onClick() {
          emits('tracking-event', {
            action: 'editorLinkClick',
            args: { label: 'Export: Markdown' },
          });

          window.open(`/recruit/download?id=${props.jobId}&format=markdown${shareKeyParam.value}`);
        },
      },
    ],
  },
  {
    name: 'copy',
    label: 'Copy to Clipboard',
    section: 1,
    available: !showSyncOrExport.value || props.isSyncable,
    children: [
      {
        name: 'copyAll',
        label: 'Copy All',
        onClick: onCopyAllFieldsClick,
      },
      {
        name: 'copyTitle',
        label: 'Copy Title',
        onClick: onCopyTitleClick,
      },
      ...fieldsWithFooterAndHeaderJoined.value.map((field) => ({
        name: camelCase(`copy ${field.label}`),
        label: field.label,
        onClick() {
          emits('tracking-event', {
            action: 'editorLinkClick',
            args: { label: `Copy: ${field.label}` },
          });

          onCopyFieldClick(field.id, field.label);
        },
      })),
    ],
  },
  {
    name: 'tapwage',
    label: 'View on Tapwage',
    section: 1,
    available: showTapwageMenu.value,
    onClick() {
      window.open(`/redirect/tapwage/${props.tapwageId}`, '_blank');
    },
  },
  {
    name: 'board',
    label: 'View on Job Board',
    section: 1,
    available: showTapwageMenu.value,
    onClick() {
      window.open(`/redirect/external?id=${props.tapwageId}`, '_blank');
    },
  },
  {
    name: 'integration',
    label: `Open in ${props.projectIntegrationType}`,
    section: 1,
    available: showAtsMenu.value,
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: `Open in ${props.projectIntegrationType}` },
      });

      window.open(props.atsEditUrl, '_blank', 'noopener,noreferrer');
    },
  },
  {
    name: 'copyAtsUrl',
    label: `Copy URL to ${props.projectIntegrationType}`,
    section: 1,
    available: showAtsMenu.value,
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: `Copy URL to ${props.projectIntegrationType}}` },
      });

      writeToClipboardApi(props.atsEditUrl);
      addSuccessSnackbar({ text: `${props.projectIntegrationType} URL successfully copied to clipboard`, timeout: 3500 });
    },
  },
  {
    name: 'breakdown',
    label: 'Score Breakdown',
    section: 2,
    available: checkRbacPermissions(['adminScoreBreakdown']) && !props.isSharePage,
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: 'Score Breakdown' },
      });

      window.open(`/admin/job/messages?job_id=${props.jobId}`, '_blank');
    },
  },
  {
    name: 'copyToDemo',
    label: 'Copy to Sales Demo',
    section: 2,
    available: checkRbacPermissions(['adminCopyJobToProject']) && !props.isSharePage,
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: 'Copy to Demo' },
      });

      window.open(`/admin/job/copy-to?job_id=${props.jobId}&project_id=7660`, '_blank');
    },
  },
  {
    name: 'copyToLinguist',
    label: 'Copy to Linguist Project',
    section: 2,
    available: checkRbacPermissions(['adminCopyJobToProject']) && !props.isSharePage,
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: 'Copy to Linguist' },
      });

      window.open(`/admin/job/copy-to?job_id=${props.jobId}&project_id=5708`, '_blank');
    },
  },
  {
    name: 'copyToJobArchitecture',
    label: 'Copy to Job Architecture Demo',
    section: 2,
    available: checkRbacPermissions(['adminCopyJobToProject']) && !props.isSharePage,
    onClick() {
      emits('tracking-event', {
        action: 'editorLinkClick',
        args: { label: 'Copy to Job Architecture Demo' },
      });

      window.open(`/admin/job/copy-to?job_id=${props.jobId}&project_id=44383`, '_blank');
    },
  },
  {
    name: 'html',
    label: 'Tapwage HTML',
    section: 2,
    available: checkRbacPermissions(['adminTapwageHtml']) && props.hasIntegration
            && props.projectIntegrationTypeKey === TYPE_TAPWAGE && !props.isSharePage,
    onClick() {
      window.open(`/admin/job/tapwage-html?job_id=${props.jobId}`);
    },
  },
]);

/**
 * We remove unavaible list items and compute which
 * is the last in each section to render a divider
 */
const reducedItems = computed(() => dropdownItems.value
  .reduce(([results, last], item) => {
    const { available, section } = item;

    if (!available) {
      return [results, last];
    }

    /**
     * if current item belongs to other section than the previous item,
     * we should mark the previous item to have a divider.
     */
    if (last && last.section !== section) {
    // eslint-disable-next-line no-param-reassign
      last.hasDivider = true;
    }

    /**
     * We push { name, section, hasDivider: false } to the result and the
     * object will be `last` on the next iteration.
     */
    const config = { ...item, hasDivider: false };
    results.push(config);
    return [results, config];
  }, [[]]));

// Due to the nature of reducedItems we grab the results
const filteredDropdownItems = computed(() => reducedItems.value?.[0] || []);
</script>

<style lang="less">
.dropdown-button {
  color: fade(@grey-900, 75%);
  border-color: fade(@grey-900, 15%);
  font-weight: bold;
}
</style>
